import { Component, OnInit, OnDestroy, signal, inject, computed, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Title } from '@angular/platform-browser';
import {
  FormBuilder,
  FormGroup,
  FormControl,
  Validators,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MenuItem } from 'primeng/api';

// Service e Modelli (Generati da specifica OpenAPI)
import { QuestionnaireService } from '../../core/services/questionnaire.service';
import { ProfessionalsService } from '../../core/services/professionals.service';
import { Alert, ResponseSubmission, QuestionnaireAnswerResponse } from '../../core/models/models';
import { AuthService } from '../../core/services/auth.service';
import { ProfessionalsDataService } from '../../frontoffice/services/professionals-data.service';
import { ProfessionalsWizardService } from '../../frontoffice/services/professionals-wizard.service';
import { MessageService } from 'primeng/api';
import {
  NoSpecialCharsValidator,
  RequiredTrimmedValidator,
} from '../../core/validators/no-special-chars.validator';

// PrimeNG Components (Sintassi 2026)
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { RadioButtonModule } from 'primeng/radiobutton';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { Toast } from 'primeng/toast';
import { InputNumber } from 'primeng/inputnumber';
import { DatePicker } from 'primeng/datepicker';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ProfessionalOperationalDetailsService } from '../../backoffice/services/axa-professional-data.service';
import { addSolCodeToFileName } from '../../core/utils/file-name.utils';

@Component({
  selector: 'app-questionnaire-run',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    ProgressSpinnerModule,
    RadioButtonModule,
    CheckboxModule,
    InputTextModule,
    ButtonModule,
    TagModule,
    Toast,
    InputNumber,
    DatePicker,
    BreadcrumbModule,
  ],
  providers: [MessageService],
  templateUrl: './questionnaire-run.component.html',
  styleUrls: ['./questionnaire-run.component.scss'],
})
export class QuestionnaireRunComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly service = inject(QuestionnaireService);
  private readonly professionalsService = inject(ProfessionalsService);
  private readonly messageService = inject(MessageService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly professionalsDataService = inject(ProfessionalsDataService);
  private readonly professionalsWizardService = inject(ProfessionalsWizardService);
  private readonly professionalOperationalDetailsService = inject(ProfessionalOperationalDetailsService);
  private readonly titleService = inject(Title);

  // Identificativo univoco della sessione di compilazione
  private sessionInstanceId: string = crypto.randomUUID();
  private existingAnswerId: string | null = null;
  private instanceId: string | null = null;
  private questionnaireId: string | null = null;
  editingExistingAnswer = signal(false);

  // Identificativi utente caricati in avvio
  professionalId: string | null = null;
  trusteeType: string | null = null;
  isReviewMode = signal(false);
  reviewAlerts = signal<Alert[]>([]);
  alertActionLoading = signal<Record<string, boolean>>({});
  solCode = signal<string | null>(null);

  // Stato reattivo con Signals
  form: FormGroup = this.fb.group({});
  questionnaire = signal<any>(null);
  isLoading = signal(true);
  isSubmitting = signal(false);
  isSavingDraft = signal(false);
  breadcrumbItems = signal<MenuItem[]>([]);
  
  // Auto-save (disabilitato - solo le API comandano)
  private autoSaveInterval?: any;
  private readonly AUTO_SAVE_ENABLED = false;
  private readonly AUTO_SAVE_INTERVAL_MS = 60000; // 60 secondi
  
  // Computed: verifica se siamo in modalità read-only
  // Readonly se: siamo in review mode (backoffice visualizza un professional) OPPURE il professional ha uno stato readonly
  isReadOnly = computed(() => {
    return this.isReviewMode() || this.professionalsDataService.isReadOnly();
  });

  constructor() {
    // Effect per gestire lo stato disabled dei controlli del form
    // quando cambia la modalità readonly
    effect(() => {
      const readonly = this.isReadOnly();
      
      if (this.form && Object.keys(this.form.controls).length > 0) {
        Object.keys(this.form.controls).forEach(key => {
          const control = this.form.get(key);
          if (control) {
            if (readonly) {
              control.disable({ emitEvent: false });
            } else {
              control.enable({ emitEvent: false });
            }
          }
        });
      }
    });
  }

  ngOnInit(): void {
    this.titleService.setTitle('Compilazione Questionario - Portale Fiduciari AXA');
    this.initializeUserContext();

    // In review mode non serve caricare il wizard step del professionista
    if (this.professionalId && !this.isReviewMode()) {
      this.professionalsWizardService.loadCurrentStep(this.professionalId);
    }

    // Carica il questionario solo se abbiamo tutti i dati necessari
    // Se manca trusteeType, verrà caricato in loadTrusteeTypeFromAPI()
    if (this.professionalId && this.trusteeType) {
      this.loadQuestionnaire();
    } else if (!this.professionalId) {
      // Nessun professionalId - carica comunque il questionario ma senza recuperare risposte salvate
      console.warn('⚠️ [QuestionnaireRun] Caricamento questionario senza professionalId - modalità view-only');
      this.loadQuestionnaire();
    }
    // Se manca solo trusteeType, aspetta che loadTrusteeTypeFromAPI() lo recuperi
    
    // Auto-save disabilitato - solo le API comandano
    if (this.AUTO_SAVE_ENABLED) {
      this.startAutoSave();
    }
  }

  ngOnDestroy(): void {
    // Pulisci l'interval quando il componente viene distrutto
    if (this.autoSaveInterval) {
      clearInterval(this.autoSaveInterval);
    }
  }

  /**
   * Auto-save periodico (disabilitato - solo le API comandano)
   */
  private startAutoSave(): void {
    this.autoSaveInterval = setInterval(() => {
      // Solo se il form ha modifiche e non siamo in readonly
      if (this.form.dirty && !this.isReadOnly() && !this.isSubmitting() && !this.isSavingDraft()) {
        console.log('🔄 Auto-save bozza...');
        this.saveDraft();
      }
    }, this.AUTO_SAVE_INTERVAL_MS);
  }

  private initializeUserContext(): void {
    // PRIORITY 1: Query params dalla route (review mode - backoffice che visualizza un professional)
    const professionalIdFromRoute = this.route.snapshot.queryParamMap.get('professionalId');
    const trusteeTypeFromRoute = this.route.snapshot.queryParamMap.get('trusteeType');
    
    if (professionalIdFromRoute && trusteeTypeFromRoute) {
      console.log('👁️ [QuestionnaireRun] Review Mode - Using query params');
      this.professionalId = professionalIdFromRoute;
      this.trusteeType = trusteeTypeFromRoute;
      this.isReviewMode.set(true);
      this.loadSolCode(professionalIdFromRoute);
      console.log('✅ [QuestionnaireRun] professionalId from route:', this.professionalId);
      console.log('✅ [QuestionnaireRun] trusteeType from route:', this.trusteeType);
      return;
    }
    
    // PRIORITY 2: Token JWT (CIAM) o mock (self-service - professional che compila i propri dati)
    const userFromToken = this.authService.getCurrentUser();
    const professionalIdFromMock = sessionStorage.getItem('professional_id');
    
    this.professionalId = userFromToken?.professionalId || professionalIdFromMock;
    if (this.professionalId) {
      this.loadSolCode(this.professionalId);
    }
    
    console.log('📝 [QuestionnaireRun] Self-Service Mode');
    console.log('🔍 [QuestionnaireRun] professionalId from token:', userFromToken?.professionalId);
    console.log('🔍 [QuestionnaireRun] professionalId from mock:', professionalIdFromMock);
    console.log('✅ [QuestionnaireRun] Final professionalId:', this.professionalId);
    
    // PRIORITY 3: Recupera trusteeType da servizio o API
    this.trusteeType = this.professionalsDataService.getTipologiaFiduciario();
    
    console.log('🔍 [QuestionnaireRun] trusteeType from service:', this.trusteeType);
    
    // Se mancano i dati, prova a caricarli dall'API /auth/profile
    if (this.professionalId && !this.trusteeType) {
      console.log('⚠️ [QuestionnaireRun] trusteeType mancante, carico da API...');
      this.loadTrusteeTypeFromAPI();
    } else if (!this.professionalId) {
      console.warn('⚠️ [QuestionnaireRun] professionalId mancante - impossibile caricare questionario');
    }
  }
  
  /**
   * Carica trusteeType dall'API /auth/profile se non disponibile nel servizio
   */
  private loadTrusteeTypeFromAPI(): void {
    this.professionalsService.getAuthProfile().subscribe({
      next: (response) => {
        if (response?.type) {
          this.trusteeType = response.type;
          console.log('✅ [QuestionnaireRun] trusteeType caricato da API:', this.trusteeType);
          
          // Salva anche nel servizio per uso futuro
          this.professionalsDataService.initializeProfessionalsData(response.type, response.tipologiaCarrozzeria);
          
          // Ora che abbiamo i dati, ricarica il questionario
          this.loadQuestionnaire();
        }
      },
      error: (err) => {
        console.error('❌ [QuestionnaireRun] Errore caricamento trusteeType da API:', err);
      }
    });
  }

  private loadQuestionnaire(): void {
    const id = this.route.snapshot.paramMap.get('id');

    if (!id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: "ID Questionario mancante nell'URL",
      });
      this.isLoading.set(false);
      return;
    }

    this.questionnaireId = id;

    // In review mode carica gli alert via API — stato sempre aggiornato
    if (this.isReviewMode() && this.professionalId) {
      this.loadAlertsFromApi(id);
    }

    this.service.getQuestionnaireById(id).subscribe({
      next: (data) => {
        this.questionnaire.set(data);
        // Filtra domande cancellate logicamente — le opzioni cancellate rimangono
        // nel signal per la visualizzazione storica in readonly (getInactiveSelectedOptions)
        const activeQuestions = (data.questions || [])
          .filter((q: any) => q.deleted !== true)
          .sort((a: any, b: any) => (a.position ?? 0) - (b.position ?? 0));
        this.buildForm(activeQuestions);
        
        // Imposta il breadcrumb con il nome del questionario
        if (this.isReviewMode()) {
          this.breadcrumbItems.set([
            { label: 'Scheda Fiduciario', routerLink: '/axa/review-professional/' + this.professionalId },
            { label: data.name || 'Compilazione Questionario' }
          ]);
        } else {
          this.breadcrumbItems.set([
            { label: 'Questionari e Allegati', routerLink: '/axa/professionals-questionnaire-attachments' },
            { label: data.name || 'Compilazione Questionario' }
          ]);
        }

        // Recupera automaticamente l'answerId dal backend se il questionario è già compilato
        if (this.professionalId && this.trusteeType) {
          this.service.listQuestionnairesByProfessionalAndTrusteeType(this.professionalId, this.trusteeType)
            .subscribe({
              next: (statuses) => {
                const currentQuestionnaire = statuses.find(q => q.questionnaireId === id);
                
                console.log('📊 Stato questionario trovato:', currentQuestionnaire);
                
                if (currentQuestionnaire?.answerId) {
                  // Questionario già compilato o bozza salvata - carica le risposte
                  // form.disable() verrà chiamato DENTRO loadExistingAnswers, dopo il patchValue,
                  // per evitare che i controlli disabilitati non vengano aggiornati da PrimeNG
                  this.existingAnswerId = currentQuestionnaire.answerId;
                  this.editingExistingAnswer.set(true);
                  console.log('✅ Caricamento risposte esistenti con answerId:', this.existingAnswerId);
                  this.loadExistingAnswers(currentQuestionnaire.answerId);
                } else if (currentQuestionnaire?.status === 'DRAFT') {
                  // Bozza senza answerId (caso edge) - logga per debug
                  console.warn('⚠️ Trovata bozza senza answerId:', currentQuestionnaire);
                  // Nessuna risposta da caricare: disabilita subito se readonly
                  if (this.isReadOnly()) {
                    this.form.disable();
                  }
                } else {
                  // Nessuna risposta salvata: disabilita subito se readonly
                  if (this.isReadOnly()) {
                    this.form.disable();
                  }
                }
              },
              error: (err) => {
                console.error('❌ Errore nel recuperare lo stato del questionario:', err);
                if (this.isReadOnly()) {
                  this.form.disable();
                }
              }
            });
        } else {
          console.warn('⚠️ professionalId o trusteeType mancanti - impossibile recuperare stato');
          if (this.isReadOnly()) {
            this.form.disable();
          }
        }

        this.isLoading.set(false);
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Caricamento fallito',
        });
        this.isLoading.set(false);
      },
    });
  }

  private loadExistingAnswers(answerId: string): void {
    console.log('🔍 Caricamento risposte per answerId:', answerId);
    
    this.service.getAnswersByInstanceId(answerId).subscribe({
      next: (rawResponse: any) => {
        console.log('✅ Risposte ricevute dal backend:', rawResponse);
        // Unwrap risposta BE: { status, message, data: {...} } oppure direttamente l'oggetto
        const data: QuestionnaireAnswerResponse = rawResponse?.data ?? rawResponse;
        const answers = data.answerData || {};
        const currentQuestions = this.questionnaire()?.questions || [];
        
        // Riutilizza il sessionInstanceId se esiste già una risposta salvata
        if (data.instanceId) {
          console.log('🔄 Riutilizzo instanceId esistente:', data.instanceId);
          this.sessionInstanceId = data.instanceId;
        }

        Object.entries(answers).forEach(([questionId, answer]) => {
          const control = this.form.get(questionId);
          if (!control) {
            return;
          }

          const question = currentQuestions.find((q: any) => q.id === questionId);

          // Gestione MULTISELECT: converte il valore in array di stringhe
          if (question?.type === 'MULTISELECT') {
            let multiValues: string[] = [];
            
            if (Array.isArray(answer)) {
              // Se è già un array, converte tutti gli elementi in stringhe
              multiValues = answer.map((v: any) => String(v));
            } else if (typeof answer === 'string' && answer.trim() !== '') {
              // Se è una stringa separata da virgole, splittala
              multiValues = answer.split(',').map(v => v.trim()).filter(Boolean);
            }
            
            // Ottieni gli ID validi delle opzioni
            const availableOptionIds = (question.options ?? []).map((option: any) => String(option.id));
            
            // Filtra solo i valori che corrispondono alle opzioni disponibili
            const validValues = multiValues.filter(v => availableOptionIds.includes(v));
            
            control.setValue(validValues);
            return;
          }

          // Gestione SINGLESELECT: gestisce l'opzione "Altro"
          if (question?.type === 'SINGLESELECT' && typeof answer === 'string') {
            const availableOptionIds = (question.options ?? []).map((option: any) => String(option.id));
            if (answer !== '' && !availableOptionIds.includes(answer)) {
              control.setValue('SPECIAL_OTHER');
              const otherControl = this.form.get(`${questionId}_otherText`);
              if (otherControl) {
                otherControl.setValue(answer);
              }
              return;
            }
          }

          control.setValue(answer);
        });

        // Ripopola i campi commento per opzioni con requiresComment
        Object.entries(answers).forEach(([key, val]) => {
          if (key.includes('_comment_')) {
            const commentControl = this.form.get(key);
            if (commentControl) commentControl.setValue(val);
          }
        });

        this.form.markAsPristine();
        // Disabilita il form DOPO aver patchato i valori, così PrimeNG riceve
        // lo stato corretto prima che i controlli vengano disabilitati
        if (this.isReadOnly()) {
          this.form.disable();
        }
        console.log('✅ Form popolato con risposte esistenti');
      },
      error: (err) => {
        console.error('❌ Errore nel caricamento risposte:', err);
        this.messageService.add({
          severity: 'warn',
          summary: 'Avviso',
          detail: 'Impossibile caricare le risposte precedenti. Riprova.',
          life: 5000,
        });
      },
    });
  }

  private buildForm(questions: any[]): void {
    // Reset totale per evitare residui di vecchi questionari in memoria
    this.form = this.fb.group({});
    const group: any = {};

    questions.forEach((q) => {
      // IMPORTANTE: q.id deve essere l'UUID del backend
      const questionId = q.id;

      // WCAG FIX: All question types respect mandatory flag including MULTISELECT
      const validators = q.mandatory
        ? [q.type === 'TEXT' ? RequiredTrimmedValidator() : Validators.required]
        : [];

      // ✅ XSS VALIDATION: Aggiungi NoSpecialCharsValidator per campi TEXT
      if (q.type === 'TEXT') {
        validators.push(NoSpecialCharsValidator('free-text')); // free-text: consente accenti/apostrofi/&, blocca solo tag HTML e script (XSS)
      }

      let initialValue: any = q.type === 'MULTISELECT' ? [] : q.type === 'BOOLEAN' ? null : '';

      // Creiamo il controllo usando l'ID originale come chiave
      group[questionId] = new FormControl(initialValue, validators);

      if (q.type === 'SINGLESELECT') {
        // Campo testo libero per opzione SPECIAL_OTHER (Altro)
        group[`${questionId}_otherText`] = new FormControl('', [NoSpecialCharsValidator('free-text')]);
        // Campi commento: Validators.required applicato dinamicamente in onSubmit solo se l'opzione è selezionata
        (q.options ?? []).filter((o: any) => o.requiresComment).forEach((o: any) => {
          group[`${questionId}_comment_${o.id}`] = new FormControl('', [NoSpecialCharsValidator('free-text')]);
        });
      }

      if (q.type === 'MULTISELECT') {
        // Campi commento: Validators.required applicato dinamicamente in onSubmit solo se l'opzione è selezionata
        (q.options ?? []).filter((o: any) => o.requiresComment).forEach((o: any) => {
          group[`${questionId}_comment_${o.id}`] = new FormControl('', [NoSpecialCharsValidator('free-text')]);
        });
      }
    });

    this.form = this.fb.group(group);
  }

  // --- LOGICA SELEZIONE ---

  isOptionChecked(questionId: string, optionId: string | number): boolean {
    const values = this.form.get(questionId)?.value;
    if (!Array.isArray(values)) {
      return false;
    }
    // Converte tutti i valori in stringhe per il confronto
    const stringValues = values.map((v: any) => String(v));
    return stringValues.includes(String(optionId));
  }

  toggleMultiOption(questionId: string, optionId: string | number): void {
    const control = this.form.get(questionId);
    let currentValues: any[] = Array.isArray(control?.value) ? [...control?.value] : [];
    
    // Converte l'optionId in stringa per consistenza
    const optionIdStr = String(optionId);

    // Converte tutti i valori correnti in stringhe
    currentValues = currentValues.map((v: any) => String(v));

    if (currentValues.includes(optionIdStr)) {
      // Deseleziona
      currentValues = currentValues.filter((v) => v !== optionIdStr);
    } else {
      // Seleziona
      currentValues.push(optionIdStr);
    }

    control?.setValue(currentValues);
    control?.markAsDirty();
    control?.updateValueAndValidity({ emitEvent: true });
  }

  onMultiChoiceChange(questionId: string, optionId: string | number, isChecked: boolean): void {
    const control = this.form.get(questionId);
    let currentValues: any[] = Array.isArray(control?.value) ? [...control?.value] : [];
    
    // Converte l'optionId e tutti i valori in stringhe per consistenza
    const optionIdStr = String(optionId);
    currentValues = currentValues.map((v: any) => String(v));

    if (isChecked) {
      if (!currentValues.includes(optionIdStr)) {
        currentValues.push(optionIdStr);
      }
    } else {
      currentValues = currentValues.filter((v) => v !== optionIdStr);
    }

    control?.setValue(currentValues);
    control?.markAsDirty();
    control?.updateValueAndValidity({ emitEvent: true });
  }

  // --- HELPERS TEMPLATE ---

  getControl(id: string): FormControl {
    return this.form.get(id) as FormControl;
  }

  isQuestionType(question: any, type: string): boolean {
    return question.type === type;
  }

  /** Restituisce solo le domande non cancellate logicamente */
  getActiveQuestions(questions: any[]): any[] {
    return (questions ?? [])
      .filter((q: any) => q.deleted !== true)
      .sort((a: any, b: any) => (a.position ?? 0) - (b.position ?? 0));
  }

  /** Restituisce le opzioni attive di una domanda (active !== false) */
  getActiveOptions(question: any): any[] {
    return (question.options ?? []).filter((o: any) => o.active !== false);
  }

  /**
   * Restituisce le opzioni inattive (cancellate logicamente) per cui esiste una risposta storica.
   * Usate in readonly per mostrare la risposta selezionata anche se l'opzione non è più disponibile.
   */
  getInactiveSelectedOptions(question: any): any[] {
    if (!this.isReadOnly()) return [];
    const selectedValue = this.form.get(question.id)?.value;
    const selectedValues: string[] = Array.isArray(selectedValue) ? selectedValue : selectedValue ? [selectedValue] : [];
    return (question.options ?? []).filter((o: any) => o.active === false && selectedValues.includes(o.id));
  }

  isOtherSelected(questionId: string): boolean {
    return this.form.get(questionId)?.value === 'SPECIAL_OTHER';
  }

  /** Restituisce true se una SINGLESELECT option con requiresComment è attualmente selezionata */
  isCommentRequired(questionId: string, optionId: string): boolean {
    return this.form.get(questionId)?.value === optionId;
  }

  /** Restituisce true se una MULTISELECT option con requiresComment è attualmente selezionata */
  isMultiCommentRequired(questionId: string, optionId: string): boolean {
    const values: string[] = this.form.get(questionId)?.value ?? [];
    return values.includes(optionId);
  }

  /**
   * Controlla se ci sono campi TEXT con caratteri speciali non permessi
   * @returns Array di titoli domande con errori
   */
  private hasSpecialCharsErrors(): string[] {
    const fieldsWithErrors: string[] = [];
    const currentQuestions = this.questionnaire()?.questions;

    if (!currentQuestions) return [];

    currentQuestions.forEach((q: any) => {
      const control = this.form.get(q.id);
      if (control?.hasError('specialChars')) {
        fieldsWithErrors.push(q.text || `Domanda ${q.id}`);
      }

      // Controlla anche il campo _otherText se presente
      if (q.type === 'SINGLESELECT') {
        const otherControl = this.form.get(`${q.id}_otherText`);
        if (otherControl?.hasError('specialChars')) {
          fieldsWithErrors.push(`${q.text} (campo Altro)`);
        }
        // Campi commento per opzioni con requiresComment
        (q.options ?? []).filter((o: any) => o.requiresComment).forEach((o: any) => {
          const cc = this.form.get(`${q.id}_comment_${o.id}`);
          if (cc?.hasError('specialChars')) fieldsWithErrors.push(`${q.text} - commento "${o.text}"`);
        });
      }
      if (q.type === 'MULTISELECT') {
        (q.options ?? []).filter((o: any) => o.requiresComment).forEach((o: any) => {
          const cc = this.form.get(`${q.id}_comment_${o.id}`);
          if (cc?.hasError('specialChars')) fieldsWithErrors.push(`${q.text} - commento "${o.text}"`);
        });
      }
    });

    return fieldsWithErrors;
  }

  /**
   * Carica eventuale bozza esistente per questo questionario
   */
  private loadExistingDraft(): void {
    const professionalId = this.professionalId;
    const trusteeType = this.trusteeType;
    
    if (!professionalId || !trusteeType || !this.questionnaireId) {
      return;
    }

    // Controlla se esiste una bozza tramite l'API status
    this.service.listQuestionnairesByProfessionalAndTrusteeType(professionalId, trusteeType)
      .subscribe({
        next: (statuses) => {
          const draft = statuses.find(
            q => q.questionnaireId === this.questionnaireId && q.status === 'DRAFT'
          );
          
          if (draft?.answerId) {
            // Bozza trovata, carica i dati
            this.instanceId = draft.answerId;
            
            // Carica le risposte salvate
            this.service.getAnswersByInstanceId(draft.answerId).subscribe({
              next: (savedData: any) => {
                // Popola il form con i dati salvati
                if (savedData.answerData) {
                  Object.keys(savedData.answerData).forEach(questionId => {
                    const control = this.form.get(questionId);
                    if (control) {
                      control.setValue(savedData.answerData[questionId]);
                    }
                  });
                  
                  this.messageService.add({
                    severity: 'info',
                    summary: 'Bozza trovata',
                    detail: 'È stata ripresa una bozza salvata in precedenza.',
                    life: 4000,
                  });
                }
              },
              error: (err) => {
                console.error('Errore caricamento bozza:', err);
              }
            });
          }
        },
        error: (err) => {
          console.error('Errore verifica bozze:', err);
        }
      });
  }

  /**
   * Prendi in carico un alert (review mode)
   */
  takeInChargeAlert(alert: Alert): void {
    this.alertActionLoading.update(state => ({ ...state, [`take_${alert.id}`]: true }));
    this.professionalsService.takeInChargeAlert(alert.id).subscribe({
      next: () => {
        this.reviewAlerts.update(alerts => alerts.map(current =>
          current.id === alert.id ? { ...current, disabled: true } : current));
        this.clearAlertActionLoading(`take_${alert.id}`);
        this.messageService.add({ severity: 'success', summary: 'Avviso preso in carico', life: 3000 });
      },
      error: () => {
        this.clearAlertActionLoading(`take_${alert.id}`);
        this.messageService.add({ severity: 'error', summary: 'Errore', detail: 'Impossibile prendere in carico l\'avviso', life: 4000 });
      }
    });
  }

  /**
   * Marca come risolto un alert (review mode)
   */
  resolveAlert(alert: Alert): void {
    this.alertActionLoading.update(state => ({ ...state, [`resolve_${alert.id}`]: true }));
    this.professionalsService.resolveAlert(alert.id).subscribe({
      next: () => {
        this.reviewAlerts.update(alerts => alerts.map(current =>
          current.id === alert.id ? { ...current, resolved: true, disabled: true } : current));
        this.clearAlertActionLoading(`resolve_${alert.id}`);
        this.messageService.add({ severity: 'success', summary: 'Avviso marcato come risolto', life: 3000 });
      },
      error: () => {
        this.clearAlertActionLoading(`resolve_${alert.id}`);
        this.messageService.add({ severity: 'error', summary: 'Errore', detail: 'Impossibile risolvere l\'avviso', life: 4000 });
      }
    });
  }

  getQuestionAlerts(questionId: string): Alert[] {
    return this.reviewAlerts().filter(alert => alert.questionId === questionId);
  }

  getOptionAlerts(questionId: string, optionId: string): Alert[] {
    return this.getQuestionAlerts(questionId).filter(alert => alert.questionOptionId === optionId);
  }

  hasActiveQuestionAlert(questionId: string): boolean {
    return this.getQuestionAlerts(questionId).some(alert => !alert.resolved);
  }

  hasActiveOptionAlert(questionId: string, optionId: string): boolean {
    return this.getOptionAlerts(questionId, optionId).some(alert => !alert.resolved);
  }

  private clearAlertActionLoading(key: string): void {
    this.alertActionLoading.update(state => {
      const next = { ...state };
      delete next[key];
      return next;
    });
  }

  /**
   * Salva come bozza (DRAFT)
   */
  saveDraft(): void {
    // ✅ XSS VALIDATION: Controlla caratteri speciali
    const specialCharsFields = this.hasSpecialCharsErrors();
    if (specialCharsFields.length > 0) {
      this.form.markAllAsTouched();
      this.messageService.add({
        severity: 'error',
        summary: 'Caratteri non permessi',
        detail: `Le seguenti domande contengono caratteri speciali non permessi (< > " ' & / \\): ${specialCharsFields.join(', ')}. Correggi i campi evidenziati.`,
        life: 6000,
      });
      return;
    }

    this.saveQuestionnaire('DRAFT', false);
  }

  /**
   * Salva e prosegui (FILLED) con validazione completa
   */
  onSubmit(): void {
    // ✅ XSS VALIDATION: Controlla caratteri speciali PRIMA della validazione required
    const specialCharsFields = this.hasSpecialCharsErrors();
    if (specialCharsFields.length > 0) {
      this.form.markAllAsTouched();
      this.messageService.add({
        severity: 'error',
        summary: 'Caratteri non permessi',
        detail: `Le seguenti domande contengono caratteri speciali non permessi (< > " ' & / \\): ${specialCharsFields.join(', ')}. Correggi i campi evidenziati.`,
        life: 6000,
      });
      return;
    }

    // Applica Validators.required sui campi commento solo se l'opzione è attualmente selezionata
    const currentQuestions = this.questionnaire()?.questions ?? [];
    currentQuestions.forEach((q: any) => {
      if (q.type === 'SINGLESELECT') {
        const selectedValue = String(this.form.get(q.id)?.value ?? '');
        (q.options ?? []).filter((o: any) => o.requiresComment).forEach((o: any) => {
          const cc = this.form.get(`${q.id}_comment_${o.id}`);
          if (cc) {
            if (selectedValue === String(o.id)) {
              cc.setValidators([RequiredTrimmedValidator(), NoSpecialCharsValidator('free-text')]);
            } else {
              cc.setValidators([NoSpecialCharsValidator('free-text')]);
            }
            cc.updateValueAndValidity({ emitEvent: false });
          }
        });
      }
      if (q.type === 'MULTISELECT') {
        const selectedValues: string[] = (this.form.get(q.id)?.value ?? []).map((v: any) => String(v));
        (q.options ?? []).filter((o: any) => o.requiresComment).forEach((o: any) => {
          const cc = this.form.get(`${q.id}_comment_${o.id}`);
          if (cc) {
            if (selectedValues.includes(String(o.id))) {
              cc.setValidators([RequiredTrimmedValidator(), NoSpecialCharsValidator('free-text')]);
            } else {
              cc.setValidators([NoSpecialCharsValidator('free-text')]);
            }
            cc.updateValueAndValidity({ emitEvent: false });
          }
        });
      }
    });

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.messageService.add({
        severity: 'warn',
        summary: 'Campi obbligatori',
        detail: 'Completa tutti i campi obbligatori prima di proseguire.',
        life: 4000,
      });
      return;
    }

    this.saveQuestionnaire('FILLED', true);
  }

  /**
   * Metodo generico per salvare il questionario
   */
  private saveQuestionnaire(status: 'DRAFT' | 'FILLED', navigateAfter: boolean): void {
    const qId = this.route.snapshot.paramMap.get('id');
    const currentQuestions = this.questionnaire()?.questions;
    
    if (!qId || !currentQuestions) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Dati questionario non disponibili',
      });
      return;
    }

    if (!this.professionalId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Professional ID non disponibile. Effettua nuovamente il login.',
      });
      return;
    }

    const isDraft = status === 'DRAFT';
    
    if (!this.trusteeType) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Tipo fiduciario non disponibile. Effettua nuovamente il login.',
      });
      if (isDraft) {
        this.isSavingDraft.set(false);
      } else {
        this.isSubmitting.set(false);
      }
      return;
    }

    // Imposta il flag di loading appropriato
    if (isDraft) {
      this.isSavingDraft.set(true);
    } else {
      this.isSubmitting.set(true);
    }

    const rawValues = this.form.value;
    const cleanedAnswers: { [key: string]: any } = {};

    // CICLO SOLO SULLE DOMANDE REALI RICEVUTE DAL BE
    currentQuestions.forEach((q: any) => {
      const questionId = q.id;
      let value = rawValues[questionId];

      // Gestione speciale per l'opzione "ALTRO"
      if (value === 'SPECIAL_OTHER') {
        const customText = rawValues[`${questionId}_otherText`];
        value = customText && customText.trim() !== '' ? customText : 'ALTRO';
      }

      // Campi commento per opzioni con requiresComment: invia solo quelli
      // delle opzioni attualmente selezionate, senza pulire lo stato locale del form.
      if (q.type === 'SINGLESELECT') {
        const selectedValue = String(rawValues[questionId] ?? '');
        (q.options ?? []).filter((opt: any) => opt.requiresComment).forEach((opt: any) => {
          if (selectedValue !== String(opt.id)) {
            return;
          }

          const commentKey = `${questionId}_comment_${opt.id}`;
          const commentValue = rawValues[commentKey];
          if (commentValue !== undefined) {
            cleanedAnswers[commentKey] = commentValue ?? '';
          }
        });
      }

      if (q.type === 'MULTISELECT') {
        const selectedValues: string[] = Array.isArray(rawValues[questionId])
          ? rawValues[questionId].map((selected: any) => String(selected))
          : [];

        (q.options ?? []).filter((opt: any) => opt.requiresComment).forEach((opt: any) => {
          if (!selectedValues.includes(String(opt.id))) {
            return;
          }

          const commentKey = `${questionId}_comment_${opt.id}`;
          const commentValue = rawValues[commentKey];
          if (commentValue !== undefined) {
            cleanedAnswers[commentKey] = commentValue ?? '';
          }
        });
      }

      value = this.normalizeAnswerValue(q, value);

      if (this.shouldIncludeAnswer(q, value)) {
        cleanedAnswers[questionId] = value;
      }
    });

    const payload: ResponseSubmission = {
      questionnaireId: qId,
      instanceId: this.sessionInstanceId,
      answers: cleanedAnswers,
      professionalId: this.professionalId,
      trusteeType: this.trusteeType,
      status: status,
    };

    this.service.submitAnswers(payload).subscribe({
      next: (response: any) => {
        // ✅ CRITICAL FIX: Parse response se è una stringa
        let parsedResponse = response;
        if (typeof response === 'string') {
          try {
            parsedResponse = JSON.parse(response);
            console.log('📦 Response parsed:', parsedResponse);
          } catch (e) {
            console.warn('⚠️ Response non è JSON valido:', response);
            parsedResponse = {};
          }
        }
        
        // Salva instanceId e answerId dalla risposta del backend
        if (!this.instanceId && parsedResponse?.instanceId) {
          this.instanceId = parsedResponse.instanceId;
        }
        
        // ✅ CRITICAL FIX: Salva answerId per permettere il reload al refresh
        if (parsedResponse?.answerId) {
          this.existingAnswerId = parsedResponse.answerId;
          console.log('✅ AnswerId salvato:', this.existingAnswerId);
        }

        if (isDraft) {
          this.isSavingDraft.set(false);
          this.messageService.add({
            severity: 'success',
            summary: 'Bozza salvata',
            detail: 'Puoi riprendere la compilazione in qualsiasi momento.',
            life: 3000,
          });
        } else {
          this.isSubmitting.set(false);
          this.messageService.add({
            severity: 'success',
            summary: 'Completato',
            detail: 'Questionario salvato con successo.',
            life: 3000,
          });

          if (navigateAfter) {
            setTimeout(() => {
              this.router.navigate(['/axa/professionals-questionnaire-attachments']);
            }, 1500);
          }
        }
      },
      error: (err) => {
        if (isDraft) {
          this.isSavingDraft.set(false);
        } else {
          this.isSubmitting.set(false);
        }
        
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: isDraft ? 'Impossibile salvare la bozza' : 'Impossibile salvare il questionario',
          life: 4000,
        });
      },
    });
  }

  private normalizeAnswerValue(question: any, value: any): any {
    if (question?.type === 'MULTISELECT') {
      return Array.isArray(value)
        ? value.map((selected: any) => String(selected)).filter((selected: string) => selected.trim() !== '')
        : [];
    }

    if (typeof value === 'string') {
      return value.trim();
    }

    return value;
  }

  private shouldIncludeAnswer(question: any, value: any): boolean {
    switch (question?.type) {
      case 'BOOLEAN':
        return typeof value === 'boolean';
      case 'MULTISELECT':
        return Array.isArray(value) && value.length > 0;
      case 'NUMBER':
        return value !== null && value !== undefined && value !== '';
      default:
        return value !== null && value !== undefined && value !== '';
    }
  }

  /**
   * Download PDF del questionario compilato
   */
  downloadPdf(): void {
    const pdfInstanceId = this.instanceId ?? this.sessionInstanceId ?? this.existingAnswerId;

    if (!this.questionnaireId || !pdfInstanceId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Impossibile scaricare il PDF: questionario non compilato',
        life: 4000,
      });
      return;
    }

    this.service.downloadPdf(this.questionnaireId, pdfInstanceId).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const questionnaireName = this.questionnaire()?.name || 'questionario';
        const fileName = addSolCodeToFileName(
          `${questionnaireName.replace(/[^a-zA-Z0-9\s.\-_]/g, '_')}.pdf`,
          this.solCode(),
        );
        
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        
        window.URL.revokeObjectURL(url);
        
        this.messageService.add({
          severity: 'success',
          summary: 'Download completato',
          detail: `PDF "${questionnaireName}" scaricato con successo`,
          life: 3000,
        });
      },
      error: (err) => {
        console.error('Errore download PDF:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile scaricare il PDF',
          life: 4000,
        });
      },
    });
  }

  private loadSolCode(professionalId: string): void {
    this.professionalOperationalDetailsService.getOperationalDetails(professionalId).subscribe({
      next: (response: any) => {
        const data = response?.data ?? response;
        this.solCode.set(data?.otherFields?.solCode ?? data?.solCode ?? null);
      },
      error: () => this.solCode.set(null),
    });
  }

  /**
   * Navigate back to questionnaire attachments page
   */
  /**
   * Carica gli alert dal BE per questo questionario+professional (review mode).
   */
  private loadAlertsFromApi(questionnaireId: string): void {
    this.professionalsService.getAlertsByQuestionnaire(this.professionalId!, questionnaireId).subscribe({
      next: (alerts) => this.reviewAlerts.set(alerts),
      error: (err) => {
        console.error('❌ [QuestionnaireRun] Errore caricamento alert:', err);
      }
    });
  }

  goBack(): void {
    if (this.isReviewMode()) {
      this.router.navigate(['/axa/review-professional', this.professionalId]).then(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    } else {
      this.router.navigate(['/axa/professionals-questionnaire-attachments']).then(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }
  }
}
